import { useMemo } from "react";

import ReactEChartsCore from "echarts-for-react/lib/core";
import * as echarts from "echarts/core";

import {
  PieChart,
} from "echarts/charts";

import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DatasetComponent
} from "echarts/components";

import {
  SVGRenderer
} from "echarts/renderers";

import type { MutableRefObject } from "react";
import type EChartsReactCore from "echarts-for-react";
import { ECBasicOption } from "echarts/types/dist/shared";

type ChartData = Array<{
  name: string
  value: number
}> | undefined;

type StatusChartProps = {
  darkMode: boolean
  chartData: ChartData
  total: number
  assetName: string
  enableHorizontalLegend: boolean
  chartRef: MutableRefObject<EChartsReactCore | null>
  initialSelected?: {
    [name: string]: boolean
  }
}

interface LegendSelectChangedPayload extends echarts.Payload {
  type: "legendselectchanged";
  name: string;
  selected: {
    [name: string]: boolean;
  };
}

echarts.use([TitleComponent, TooltipComponent, LegendComponent, GridComponent, DatasetComponent, PieChart, SVGRenderer]);

const getNumberSelected = (chartData: ChartData, selected: {[name: string]: boolean}, total: number): number => {

  // Because the number of deselected elements is usually fewer than selected, we can perform this function
  // performantly by iterating over the deselected elements and subtracting their values from the total
  const deselectedLegendElements = (selected !== undefined)
    ? Object.entries(selected)
      .filter(([key, value]) => value === false)
      .map(([key, value]) => key)
    : [];

  return deselectedLegendElements?.reduce(
    (accumulator: number, key: string) => {
      return accumulator - (
        chartData?.find((obj: { name: string, value: number }) => obj.name === key)?.value ?? 0
      );
    },
    total || 0
  );
};

export default function StatusChart({ darkMode, chartData, initialSelected = {}, total, assetName, enableHorizontalLegend, chartRef }: StatusChartProps) {
  
  const totalChargersChartOptions: ECBasicOption = useMemo(() => {
    return {
      title: {
        text: getNumberSelected(chartData, initialSelected, total).toString(),
        subtext: (chartData != null) ? assetName : "",
        left: enableHorizontalLegend ? "49%" : "29%",
        top: "center",
        textAlign: "center",
        textStyle: {
          fontSize: 55,
          color: darkMode ? "#fff" : "#464646",
        },
        subtextStyle: {
          fontSize: 13,
          fontWeight: "bold",
          color: darkMode ? "#d6d6d6" : "#333",
        },
        itemGap: -16
      },
      tooltip: {
        trigger: "item"
      },
      legend: enableHorizontalLegend
        ? {
          type: "scroll",
          orient: "horizontal",
          show: true,
          bottom: 0,
          selected: initialSelected
        }
        : {
          type: "scroll",
          orient: "vertical",
          show: true,
          right: 0,
          top: 30,
          bottom: 0,
          selected: initialSelected
        },
      series: [
        {
          name: "Charger Status",
          type: "pie",
          radius: ["54%", "72%"],
          center: enableHorizontalLegend ? ["50%", "50%"] : ["30%", "50%"],
          left: 0,
          top: 0,
          bottom: 0,
          avoidLabelOverlap: true,
          itemStyle: {
            borderColor: darkMode ? "#212529" : "#fff",
            borderWidth: 3
          },
          label: {
            show: false,
          },
          labelLine: {
            show: false
          },
          data: chartData
        }
      ]
    };
  }, [chartData, initialSelected, total, assetName, enableHorizontalLegend, darkMode]);

  chartRef.current?.getEchartsInstance().on("legendselectchanged", (params: unknown) => {
    const legendSelectChangedPayload = params as LegendSelectChangedPayload;
    // const deselectedLegendElements = (legendSelectChangedPayload?.selected !== undefined)
    //   ? Object.entries(legendSelectChangedPayload?.selected)
    //     .filter(([key, value]) => value === false)
    //     .map(([key, value]) => key)
    //   : [];

    chartRef.current?.getEchartsInstance().setOption({
      title: {
        text: getNumberSelected(chartData, legendSelectChangedPayload?.selected, total).toString()
      }
    });
  });

  return (
    <div id="StatusChart" style={{width: "100%"}}>
      <ReactEChartsCore
        echarts={echarts}
        ref={chartRef}
        option={totalChargersChartOptions}
        style={{width: "100%"}}
        opts={{renderer: "svg"}}
      />
    </div>
  );
}